import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Paper, Table, TableContainer } from '@mui/material';
import { useSnackbar } from 'notistack';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmationDialog } from '../confirmation-dialog';
import { KPI, PSAO } from '../types/rebates';
import { RebateType } from '../types/settings';
import EmptyRebates from './EmptyRebates';
import TableHeader from './TableHeader';
import TableRows from './TableRows';
import TableToolbar from './TableToolbar';
const RebateTierTable = ({ getRebatesByPharmacyId, putRebatesByPharmacyId, patchRebateByPharmacyId, deleteRebateByPharmacyId, primaryWholesaler, psao = PSAO.APCI, kpi = KPI.GCR, pharmacyId, notifyForUpdates, pvaType, }) => {
    const { enqueueSnackbar, } = useSnackbar();
    const [rebateType, setRebateType] = useState(RebateType.GENERIC);
    const [syncing, setSyncing] = useState(false);
    const [fetching, setFetching] = useState(true);
    const [addTierPressed, setAddTierPressed] = useState(false);
    const [removeDialogOpen, setDrugDialogOpen] = useState(false);
    const [isRemoveRow, setIsRemoveRow] = useState(true);
    const [stagedRemoveId, setStagedRemoveId] = useState(-1);
    const generic = rebateType === RebateType.GENERIC;
    const INDEX_ZERO = 0;
    const SPEND_SENTINEL = 100000000;
    const tableRef = useRef(null);
    const initData = useMemo(() => [
        { id: 0, cells: [{ value: `${kpi} %`, editable: false }, { value: '$0-$10,000', editable: false }] },
        { id: 1, cells: [{ value: '0-10%', editable: false }, { value: '1', editable: false }] },
    ], []);
    const [rows, setRows] = useState([]);
    const [previousCellValue, setPreviousCellValue] = useState('');
    const parseSpendRange = (range) => {
        const matches = range.match(/\$([\d,]+)-?\$?([\d,]+)?\+?/);
        if (!matches) {
            throw new Error(`invalid spend range format: ${range}`);
        }
        const lowerSpend = parseInt(matches[1].replace(/,/g, ''), 10);
        const upperSpend = matches[0].endsWith('+')
            ? SPEND_SENTINEL
            : parseInt(matches[2].replace(/,/g, ''), 10);
        return { lowerSpend, upperSpend };
    };
    const parsePercentageRange = (range) => {
        const matches = range.match(/^(\d+(\.\d+)?)-(\d+(\.\d+)?)%$|^(\d+(\.\d+)?)%\+$/);
        if (!matches) {
            throw new Error(`invalid percentage range format: ${range}`);
        }
        const isLastRow = matches[1] === undefined && matches[0].includes('+');
        const lowerPercentage = isLastRow ? parseFloat(matches[5]) : parseFloat(matches[1]);
        const upperPercentage = isLastRow ? 100 : parseFloat(matches[3]);
        return { lowerPercentage, upperPercentage };
    };
    const isValidCell = (row, column, updatedCell) => {
        if (row > 0) {
            if (column > 0) {
                // VALIDATING REBATE CELL - must be percentage format (0-100)
                const cellValue = updatedCell.value;
                // Check for empty value
                if (cellValue === '' || cellValue.trim() === '') {
                    return false;
                }
                const value = Number(cellValue);
                if (isNaN(value) || value > 100 || value < 0) {
                    return false;
                }
                // Check decimal precision (max 2 decimal places)
                const decimalPlaces = (cellValue.split('.')[1] || '').length;
                if (decimalPlaces > 2) {
                    return false;
                }
                return true;
            }
            // VALIDATING GCR BUCKET CELL
            const matches = updatedCell.value.match(/^(\d+(\.\d+)?)-(\d+(\.\d+)?)%$|^(\d+(\.\d+)?)%\+$/);
            return matches !== null && matches.length > 0;
        }
        // VALIDATING SPEND BUCKET CELL
        const matches = updatedCell.value.match(/\$([\d,]+)-?\$?([\d,]+)?\+?/);
        return matches !== null && matches.length > 0;
    };
    const saveCellUpdates = async (row, column, updatedCell, currentRows) => {
        if (!isValidCell(row, column, updatedCell)) {
            // Don't save if validation fails
            setSyncing(false);
            return;
        }
        setSyncing(true);
        try {
            // Use the passed currentRows if available, otherwise fall back to state
            const rowsToUse = currentRows || rows;
            const thisRow = rowsToUse.at(row);
            const data = {
                matrixPosition: { row, column, },
                primaryWholesaler,
                psao,
                kpi,
                pvaType,
            };
            notifyForUpdates();
            if (row > 0) {
                // Get the updated cell value if it's the GCR bucket being edited
                const gcrBucketCell = column === INDEX_ZERO ? updatedCell : thisRow.cells.at(INDEX_ZERO);
                const { lowerPercentage, upperPercentage } = parsePercentageRange(gcrBucketCell.value);
                data.lowerPercentage = lowerPercentage;
                data.upperPercentage = upperPercentage;
                if (column > 0) {
                    // UPDATING REBATE CELL
                    const spendBucketCell = rowsToUse.at(INDEX_ZERO).cells.at(column);
                    const { upperSpend, lowerSpend } = parseSpendRange(spendBucketCell.value);
                    data.lowerSpend = lowerSpend;
                    data.upperSpend = upperSpend;
                    // User enters percentage values (e.g., 40 for 40%, 1.25 for 1.25%)
                    // Convert to decimal format for backend storage (40 → 0.40, 1 → 0.01)
                    data.rebate = Number(updatedCell.value) / 100;
                }
                else {
                    // UPDATING GCR BUCKET CELL
                    await putRebatesByPharmacyId(pharmacyId, data, { params: { bucket: 'gcr', generic: generic.toString() } });
                    return setSyncing(false);
                }
            }
            else {
                // UPDATING SPEND BUCKET CELL
                const { upperSpend, lowerSpend } = parseSpendRange(updatedCell.value);
                data.lowerSpend = lowerSpend;
                data.upperSpend = upperSpend;
                await putRebatesByPharmacyId(pharmacyId, data, { params: { bucket: 'spend', generic: generic.toString() } });
                return setSyncing(false);
            }
            await patchRebateByPharmacyId(pharmacyId, data, { params: { generic: generic.toString() } });
            setSyncing(false);
        }
        catch (error) {
            if (!error?.message?.includes('invalid')) {
                enqueueSnackbar('Failed to save rebate table updates', { variant: 'error', });
            }
            setSyncing(false);
        }
    };
    const deleteRebatesByRowOrColumn = async (row, id) => {
        setSyncing(true);
        try {
            const bucket = row ? 'gcr' : 'spend';
            await deleteRebateByPharmacyId(pharmacyId, { params: { bucket, id, generic: generic.toString() } });
            await fetchRebatesMatrix();
            setSyncing(false);
        }
        catch (_error) {
            enqueueSnackbar('Failed to save rebate table updates', { variant: 'error', });
            setSyncing(false);
        }
    };
    const fetchRebatesMatrix = async () => {
        setFetching(true);
        try {
            const isMatrix = true;
            const matrix = await getRebatesByPharmacyId(pharmacyId, { params: { generic: generic.toString() } }, isMatrix);
            if (matrix.length > 0) {
                setRows(matrix.map((row, index) => ({
                    id: index,
                    cells: row.map((cell, cellIndex) => {
                        // Backend already returns rebate values as percentages (multiplied by 100)
                        // No additional conversion needed - use values as-is
                        return { value: cell, editable: false };
                    }),
                })));
            }
            else {
                setRows([]);
            }
            setFetching(false);
        }
        catch (error) {
            setFetching(false);
        }
    };
    useEffect(() => {
        fetchRebatesMatrix();
    }, [rebateType, pharmacyId]);
    useEffect(() => {
        if (addTierPressed) {
            setRows(initData);
            if (tableRef.current) {
                tableRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [addTierPressed]);
    const handleRebateTypeChange = (_event, newType) => {
        setRebateType(newType);
    };
    const handleToggleDialogOpen = () => {
        setDrugDialogOpen((open) => !open);
    };
    const handleAddRow = () => {
        const newRowId = rows.length;
        const newRow = {
            id: newRowId,
            cells: Array(rows[0].cells.length).fill(null).map((_, index) => ({
                value: '',
                editable: index === 0, // Make first cell (GCR range) editable immediately
            })),
        };
        setRows((prevRows) => {
            const updatedRows = [...prevRows, newRow];
            // Scroll to the new row after it's added
            setTimeout(() => {
                const newRowElement = document.querySelector(`[data-row-id="${newRowId}"]`);
                if (newRowElement) {
                    newRowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 50);
            return updatedRows;
        });
        // Show hint about percentage format
        enqueueSnackbar('Enter rebate percentage values (e.g., 40 for 40%, 1.25 for 1.25%, up to 2 decimal places)', { variant: 'info' });
    };
    const handleAddColumn = () => {
        setRows((prevRows) => prevRows.map((row) => ({
            ...row,
            cells: [...row.cells, { value: '', editable: false, }],
        })));
    };
    const onRemoveRowOrColumn = () => {
        if (isRemoveRow) {
            setRows((prevRows) => prevRows.filter((row) => row.id !== stagedRemoveId));
            deleteRebatesByRowOrColumn(true, stagedRemoveId);
        }
        else {
            setRows((prevRows) => prevRows.map((row) => {
                const newCells = row.cells;
                newCells.splice(stagedRemoveId, 1);
                return { ...row, cells: newCells, };
            }));
            deleteRebatesByRowOrColumn(false, stagedRemoveId);
        }
    };
    const handleRemoveRow = (rowId) => {
        setIsRemoveRow(true);
        setDrugDialogOpen(true);
        setStagedRemoveId(rowId);
    };
    const handleRemoveColumn = (cellIndex) => {
        setIsRemoveRow(false);
        setDrugDialogOpen(true);
        setStagedRemoveId(cellIndex);
    };
    const updateCell = (rowId, cellIndex, updates, save = false) => {
        setRows((prevRows) => {
            const newRows = prevRows.map((row) => {
                if (row.id === rowId) {
                    const updatedCell = { ...row.cells[cellIndex], ...updates };
                    return {
                        ...row,
                        cells: [
                            ...row.cells.slice(0, cellIndex),
                            updatedCell,
                            ...row.cells.slice(cellIndex + 1),
                        ],
                    };
                }
                return row;
            });
            // If we need to save, pass the updated rows to saveCellUpdates
            if (save) {
                const updatedRow = newRows.find(row => row.id === rowId);
                if (updatedRow) {
                    const updatedCell = updatedRow.cells[cellIndex];
                    saveCellUpdates(rowId, cellIndex, updatedCell, newRows);
                }
            }
            return newRows;
        });
    };
    const handleCellChange = (rowId, cellIndex, value) => {
        // Update the cell value
        updateCell(rowId, cellIndex, { value });
        // For rebate cells, provide real-time validation feedback
        if (rowId > 0 && cellIndex > 0 && value !== '') {
            const numValue = Number(value);
            if (!isNaN(numValue) && numValue > 100) {
                // Show warning for invalid format but don't prevent typing
                enqueueSnackbar('Value must be between 0 and 100 (percentage format)', {
                    variant: 'warning',
                    autoHideDuration: 2000
                });
            }
        }
    };
    const handleCellClick = (rowId, cellIndex) => {
        // Store the current value before editing
        const currentRow = rows.find(row => row.id === rowId);
        if (currentRow) {
            setPreviousCellValue(currentRow.cells[cellIndex].value);
        }
        updateCell(rowId, cellIndex, { editable: true });
    };
    const handleCellBlur = (rowId, cellIndex) => {
        const currentRow = rows.find(row => row.id === rowId);
        if (currentRow) {
            const cellValue = currentRow.cells[cellIndex].value;
            // For rebate cells, validate before saving
            if (rowId > 0 && cellIndex > 0) {
                // Check for empty value
                if (cellValue === '' || cellValue.trim() === '') {
                    enqueueSnackbar('Rebate value cannot be empty', { variant: 'error' });
                    // Revert to previous value
                    updateCell(rowId, cellIndex, { editable: false, value: previousCellValue }, false);
                    return;
                }
                const numValue = Number(cellValue);
                if (isNaN(numValue) || numValue > 100 || numValue < 0) {
                    // Invalid format - revert to previous value
                    enqueueSnackbar('Invalid rebate format. Must be between 0 and 100 with up to 2 decimal places (e.g., 40, 1.25)', { variant: 'error' });
                    // Revert to previous value
                    updateCell(rowId, cellIndex, { editable: false, value: previousCellValue }, false);
                    return;
                }
                // Check decimal precision
                const decimalPlaces = (cellValue.split('.')[1] || '').length;
                if (decimalPlaces > 2) {
                    enqueueSnackbar('Decimal precision limited to 2 places (e.g., 1.25 is valid, 1.234 is not)', { variant: 'error' });
                    updateCell(rowId, cellIndex, { editable: false, value: previousCellValue }, false);
                    return;
                }
            }
        }
        // Valid value - save it
        updateCell(rowId, cellIndex, { editable: false }, true);
    };
    const handleCellKeyDown = (e, rowId, cellIndex) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Validate before accepting the value
            const currentRow = rows.find(row => row.id === rowId);
            if (currentRow && rowId > 0 && cellIndex > 0) {
                const cellValue = currentRow.cells[cellIndex].value;
                // Check for empty value
                if (cellValue === '' || cellValue.trim() === '') {
                    enqueueSnackbar('Rebate value cannot be empty', { variant: 'error' });
                    return; // Don't blur/save if empty
                }
                const numValue = Number(cellValue);
                if (isNaN(numValue) || numValue > 100 || numValue < 0) {
                    enqueueSnackbar('Invalid rebate format. Must be between 0 and 100 with up to 2 decimal places (e.g., 40, 1.25)', { variant: 'error' });
                    return; // Don't blur/save if invalid
                }
                // Check decimal precision
                const decimalPlaces = (cellValue.split('.')[1] || '').length;
                if (decimalPlaces > 2) {
                    enqueueSnackbar('Decimal precision limited to 2 places (e.g., 1.25 is valid, 1.234 is not)', { variant: 'error' });
                    return;
                }
            }
            handleCellBlur(rowId, cellIndex);
        }
    };
    return (_jsxs(Paper, { ref: tableRef, id: 'rebateTable', children: [_jsx(TableToolbar, { rebateType: rebateType, handleAddRow: handleAddRow, handleAddColumn: handleAddColumn, handleRebateTypeChange: handleRebateTypeChange, noData: rows.length === 0 }), rows.length > 0 ?
                _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx(TableHeader, { rows: rows, syncing: syncing, fetching: fetching, isGeneric: generic, colTitlesIndex: INDEX_ZERO, handleCellClick: handleCellClick, handleRemoveColumn: handleRemoveColumn, handleCellChange: handleCellChange, handleCellBlur: handleCellBlur, handleCellKeyDown: handleCellKeyDown }), _jsx(TableRows, { rows: rows, colTitlesIndex: INDEX_ZERO, handleCellClick: handleCellClick, handleRemoveRow: handleRemoveRow, handleCellChange: handleCellChange, handleCellBlur: handleCellBlur, handleCellKeyDown: handleCellKeyDown })] }) })
                : _jsx(EmptyRebates, { action: setAddTierPressed }), _jsx(ConfirmationDialog, { handleConfirm: onRemoveRowOrColumn, handleToggleOpen: handleToggleDialogOpen, open: removeDialogOpen, removeToggle: true, positiveActionText: `Remove ${isRemoveRow ? 'Row' : 'Column'}`, negativeActionText: 'Cancel', title: `Remove ${isRemoveRow ? 'Row' : 'Column'} Confirmation`, text: `Are you sure you want to remove this table ${isRemoveRow ? 'row' : 'column'}?`, sx: { width: 500, height: 200, margin: 'auto' } })] }));
};
export default memo(RebateTierTable);
